import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema } from '@/components/seo/seo';
import { clsx } from 'clsx';
import { councilPages, getCouncilDisplayName } from '@/lib/council-pages';
import {
  STATIC_AUTHORITY_LEAGUE_TABLE,
  type AuthorityLeagueTable,
} from '@/data/authority-league-table';

// Fuzzy matching utility - Levenshtein distance
function levenshteinDistance(a: string, b: string): number {
  const aLen = a.length;
  const bLen = b.length;
  
  // Create matrix with proper initialization
  const matrix: number[][] = Array.from({ length: bLen + 1 }, () => 
    Array.from({ length: aLen + 1 }, () => 0)
  );
  
  // Initialize first column and row
  for (let i = 0; i <= bLen; i++) {
    matrix[i]![0] = i;
  }
  for (let j = 0; j <= aLen; j++) {
    matrix[0]![j] = j;
  }
  
  // Fill the matrix
  for (let i = 1; i <= bLen; i++) {
    for (let j = 1; j <= aLen; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i]![j] = matrix[i - 1]![j - 1]!;
      } else {
        matrix[i]![j] = Math.min(
          matrix[i - 1]![j - 1]! + 1, // substitution
          matrix[i]![j - 1]! + 1,     // insertion
          matrix[i - 1]![j]! + 1      // deletion
        );
      }
    }
  }
  return matrix[bLen]![aLen]!;
}

// Fuzzy search with scoring
function fuzzyMatch(query: string, target: string): { match: boolean; score: number; indices: number[] } {
  const queryLower = query.toLowerCase();
  const targetLower = target.toLowerCase();
  
  // Exact substring match (highest priority)
  const exactIndex = targetLower.indexOf(queryLower);
  if (exactIndex !== -1) {
    const indices = Array.from({ length: query.length }, (_, i) => exactIndex + i);
    return { match: true, score: 100 - exactIndex, indices };
  }
  
  // Fuzzy match with Levenshtein distance
  const distance = levenshteinDistance(queryLower, targetLower.slice(0, Math.max(query.length + 2, 10)));
  const threshold = Math.floor(query.length / 3) + 1; // Allow ~1 typo per 3 chars
  
  if (distance <= threshold) {
    // Find approximate matching indices
    const indices: number[] = [];
    let qi = 0;
    for (let ti = 0; ti < target.length && qi < query.length; ti++) {
      const targetChar = targetLower.charAt(ti);
      const queryChar = queryLower.charAt(qi);
      if (targetChar === queryChar) {
        indices.push(ti);
        qi++;
      }
    }
    return { match: true, score: 50 - distance * 10, indices };
  }
  
  // Word start matching (e.g., "DCC" matching "Derbyshire County Council")
  const words = target.split(/\s+/);
  const wordIndices: number[] = [];
  let charOffset = 0;
  let qi = 0;
  
  for (const word of words) {
    const queryChar = queryLower.charAt(qi);
    if (qi < query.length && word.toLowerCase().startsWith(queryChar)) {
      wordIndices.push(charOffset);
      qi++;
    }
    charOffset += word.length + 1;
  }
  
  if (wordIndices.length === query.length) {
    return { match: true, score: 30, indices: wordIndices };
  }
  
  return { match: false, score: 0, indices: [] };
}

// Highlight matching characters component
function HighlightMatch({ text, indices }: { text: string; indices: number[] }) {
  if (indices.length === 0) {
    return <span>{text}</span>;
  }
  
  const indexSet = new Set(indices);
  const result: React.ReactNode[] = [];
  let currentGroup = '';
  let isHighlighted = false;
  
  for (let i = 0; i < text.length; i++) {
    const char = text.charAt(i);
    const shouldHighlight = indexSet.has(i);
    
    if (shouldHighlight !== isHighlighted) {
      if (currentGroup) {
        result.push(
          isHighlighted 
            ? <strong key={`h-${i}`} className="text-brand-700 font-bold">{currentGroup}</strong>
            : <span key={`s-${i}`}>{currentGroup}</span>
        );
      }
      currentGroup = char;
      isHighlighted = shouldHighlight;
    } else {
      currentGroup += char;
    }
  }
  
  if (currentGroup) {
    result.push(
      isHighlighted 
        ? <strong key="last-h" className="text-brand-700 font-bold">{currentGroup}</strong>
        : <span key="last-s">{currentGroup}</span>
    );
  }
  
  return <>{result}</>;
}

function RatingBadge({ rating, size = 'sm' }: { rating: 'green' | 'amber' | 'red' | null; size?: 'sm' | 'md' }) {
  if (!rating) {
    return (
      <span className={clsx(
        'inline-flex items-center justify-center rounded-lg font-medium',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        'bg-surface-100 text-surface-500'
      )}>
        —
      </span>
    );
  }

  const styles = {
    green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-rose-100 text-rose-800 border-rose-200',
  };

  const labels = {
    green: 'Green',
    amber: 'Amber',
    red: 'Red',
  };

  return (
    <span className={clsx(
      'inline-flex items-center justify-center rounded-lg font-semibold border',
      size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
      styles[rating]
    )}>
      {labels[rating]}
    </span>
  );
}

function StatCard({ value, label, color }: { value: number; label: string; color: 'green' | 'amber' | 'red' | 'total' }) {
  const bgColors = {
    green: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-rose-500 to-rose-600',
    total: 'from-brand-600 to-brand-700',
  };

  return (
    <div className={clsx(
      'rounded-2xl p-6 text-white bg-gradient-to-br',
      bgColors[color]
    )}>
      <div className="text-4xl font-bold">{value}</div>
      <div className="text-sm opacity-90 mt-1">{label}</div>
    </div>
  );
}

export function AuthorityRatingsPage() {
  const [data] = useState<AuthorityLeagueTable>(STATIC_AUTHORITY_LEAGUE_TABLE);
  const [searchParams] = useSearchParams();
  const [selectedRegion, setSelectedRegion] = useState<string>(() => {
    const region = searchParams.get('region');
    return region && STATIC_AUTHORITY_LEAGUE_TABLE.regions.includes(region) ? region : 'all';
  });
  const [selectedRating, setSelectedRating] = useState<'all' | 'green' | 'amber' | 'red'>(() => {
    const rating = searchParams.get('rating');
    return rating === 'green' || rating === 'amber' || rating === 'red' ? rating : 'all';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'overall'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Close autocomplete when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        autocompleteRef.current && 
        !autocompleteRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowAutocomplete(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Autocomplete suggestions with fuzzy matching
  const autocompleteSuggestions = useMemo(() => {
    if (!data || searchQuery.length < 3) return [];
    
    const results = data.authorities
      .map(auth => {
        const result = fuzzyMatch(searchQuery, auth.name);
        return { ...auth, ...result };
      })
      .filter(item => item.match)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8); // Limit to 8 suggestions
    
    return results;
  }, [data, searchQuery]);
  
  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setShowAutocomplete(value.length >= 3);
    setHighlightedIndex(-1);
  }, []);
  
  // Handle autocomplete selection
  const handleSelectSuggestion = useCallback((name: string) => {
    setSearchQuery(name);
    setShowAutocomplete(false);
    setHighlightedIndex(-1);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showAutocomplete || autocompleteSuggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && autocompleteSuggestions[highlightedIndex]) {
          handleSelectSuggestion(autocompleteSuggestions[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setHighlightedIndex(-1);
        break;
    }
  }, [showAutocomplete, autocompleteSuggestions, highlightedIndex, handleSelectSuggestion]);
  
  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setShowAutocomplete(false);
    searchInputRef.current?.focus();
  }, []);

  // Filter and sort authorities
  const filteredAuthorities = data?.authorities.filter(auth => {
    const matchesRegion = selectedRegion === 'all' || auth.region === selectedRegion;
    const matchesRating = selectedRating === 'all' || auth.dftOverallRating === selectedRating;
    const matchesSearch = !searchQuery || 
      auth.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRegion && matchesRating && matchesSearch;
  }).sort((a, b) => {
    if (sortBy === 'name') {
      return sortOrder === 'asc' 
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else {
      // Sort by rating: green > amber > red
      const ratingOrder = { green: 1, amber: 2, red: 3, null: 4 };
      const aOrder = ratingOrder[a.dftOverallRating as keyof typeof ratingOrder] || 4;
      const bOrder = ratingOrder[b.dftOverallRating as keyof typeof ratingOrder] || 4;
      return sortOrder === 'asc' ? aOrder - bOrder : bOrder - aOrder;
    }
  }) || [];

  // Calculate filtered stats
  const filteredStats = {
    total: filteredAuthorities.length,
    green: filteredAuthorities.filter(a => a.dftOverallRating === 'green').length,
    amber: filteredAuthorities.filter(a => a.dftOverallRating === 'amber').length,
    red: filteredAuthorities.filter(a => a.dftOverallRating === 'red').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-50 to-white">
      <SEO
        title="Search English Council Road Ratings 2025-26 | Fixtyer"
        description="Search all 154 English highway authorities in the official DfT 2025-2026 road maintenance bands: 13 RED, 125 AMBER and 16 GREEN."
        canonical="/authority-ratings"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Council Ratings', url: '/authority-ratings' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'What do the DfT road maintenance ratings mean?',
            answer: 'The Department for Transport rates local highway authorities as Red, Amber, or Green using published road condition, spending and best-practice criteria. The rating describes network-level performance, not a finding about a specific defect.',
          },
          {
            question: 'How do these ratings affect my pothole claim?',
            answer: 'A rating may provide background context, but it is not proof of liability. It does not show what the authority knew about a specific pothole and does not automatically defeat a Section 58 defence.',
          },
          {
            question: 'Where does this data come from?',
            answer: 'All data comes from the official Department for Transport Local Road Maintenance Ratings 2025-2026, published on GOV.UK and based on council transparency reports.',
          },
          {
            question: 'Why are some councils rated Red while others are Green?',
            answer: 'Ratings reflect published road condition, spending and best-practice criteria. They are comparative network-level indicators and do not decide individual pothole claims.',
          },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900 text-white">
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-6 backdrop-blur">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Official DfT Data • {data?.ratingYear || '2025-2026'}
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              English Council Road{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-accent-400">
                Maintenance Ratings
              </span>
            </h1>
            
            <p className="mt-6 text-lg text-brand-100 max-w-2xl">
              Search all 154 English highway authorities in the DfT 2025-2026 ratings: 13 RED,
              125 AMBER and 16 GREEN. Ratings provide network-level context and do not establish
              notice, breach or liability for an individual incident.
            </p>
            <p className="mt-4 text-sm font-medium text-brand-200">Last reviewed: 19 July 2026</p>
            <nav className="mt-5 flex flex-wrap gap-4 text-sm" aria-label="Related council data">
              <Link className="font-semibold text-white underline" to="/councils">Find your council report</Link>
              <Link className="font-semibold text-white underline" to="/road-maintenance-report">Read the national report</Link>
              <Link className="font-semibold text-white underline" to="/data/council-road-maintenance-ratings">Dataset and methodology</Link>
              <Link className="font-semibold text-white underline" to="/pothole-hall-of-shame">See the DfT RED list</Link>
            </nav>
          </div>

          {/* Stats */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              <StatCard value={data.stats.total} label="Authorities Rated" color="total" />
              <StatCard value={data.stats.green} label="Green Rating" color="green" />
              <StatCard value={data.stats.amber} label="Amber Rating" color="amber" />
              <StatCard value={data.stats.red} label="Red Rating" color="red" />
            </div>
          )}
        </div>
      </section>

      {/* Explanation Section */}
      <section className="py-12 bg-white border-b border-surface-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-100">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <span className="text-2xl">✓</span>
              </div>
              <h3 className="font-display text-lg font-semibold text-emerald-900 mb-2">
                Green Rating
              </h3>
              <p className="text-sm text-emerald-800">
                A higher network-level assessment under the DfT criteria. It does not establish how a
                specific road was inspected or repaired.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-amber-50 border border-amber-100">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="font-display text-lg font-semibold text-amber-900 mb-2">
                Amber Rating
              </h3>
              <p className="text-sm text-amber-800">
                A middle network-level assessment under the DfT criteria. The specific inspection and
                repair records for the defect remain more relevant to an individual claim.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-rose-50 border border-rose-100">
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center mb-4">
                <span className="text-2xl">⛔</span>
              </div>
              <h3 className="font-display text-lg font-semibold text-rose-900 mb-2">
                Red Rating
              </h3>
              <p className="text-sm text-rose-800">
                A lower network-level assessment under the DfT criteria. It does not by itself establish
                notice, a breach of duty, or liability for a specific defect.
              </p>
            </div>
          </div>
          
          <div className="mt-8 p-6 rounded-2xl bg-brand-50 border border-brand-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">💡</span>
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-brand-900 mb-2">
                  What This Means For Your Claim
                </h3>
                <p className="text-sm text-brand-800">
                  Ratings provide context, not proof of liability, and do not automatically defeat a Section 58 defence.
                  An individual claim turns on the specific defect, causation, notice, inspections and repairs.
                  Fixtyer includes the source rating as background information for you to review.
                </p>
              </div>
            </div>
          </div>
          <p className="mt-6 text-sm text-surface-700">
            For a West Midlands example, see{' '}
            <Link className="font-semibold text-brand-700 underline" to="/councils/dudley">
              Dudley&apos;s AMBER, GREEN and RED rating bands
            </Link>
            . Its council page also separates network condition surveys from location-specific
            safety-inspection records.
          </p>
        </div>
      </section>

      {/* League Table Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <>
              {/* Enhanced Search with Autocomplete */}
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="flex-1 relative">
                  <label className="sr-only" htmlFor="council-search">Search authorities</label>
                  <div className="relative">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      ref={searchInputRef}
                      id="council-search"
                      type="text"
                      placeholder="Search by council name..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={() => searchQuery.length >= 3 && setShowAutocomplete(true)}
                      onKeyDown={handleKeyDown}
                      autoComplete="off"
                      aria-expanded={showAutocomplete}
                      aria-haspopup="listbox"
                      aria-controls="council-suggestions"
                      className="w-full pl-12 pr-10 py-3 min-h-[48px] rounded-xl border border-surface-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                    />
                    {searchQuery && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600 transition-colors"
                        aria-label="Clear search"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Autocomplete Dropdown */}
                  {showAutocomplete && (
                    <div 
                      ref={autocompleteRef}
                      id="council-suggestions"
                      role="listbox"
                      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-surface-200 z-50 overflow-hidden max-h-[400px] overflow-y-auto"
                    >
                      {autocompleteSuggestions.length > 0 ? (
                        autocompleteSuggestions.map((suggestion, index) => {
                          const hasPage = councilPages[suggestion.name];
                          const displayName = getCouncilDisplayName(suggestion.name);
                          return (
                            <button
                              key={suggestion.id}
                              role="option"
                              aria-selected={index === highlightedIndex}
                              onClick={() => handleSelectSuggestion(suggestion.name)}
                              onMouseEnter={() => setHighlightedIndex(index)}
                              className={clsx(
                                'w-full px-4 py-3 flex items-center justify-between gap-4 text-left transition-colors',
                                index === highlightedIndex ? 'bg-brand-50' : 'hover:bg-surface-50'
                              )}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-brand-900 truncate">
                                  <HighlightMatch text={displayName} indices={suggestion.indices} />
                                </div>
                                <div className="text-xs text-surface-500 truncate">
                                  {suggestion.region}
                                  {hasPage && (
                                    <span className="ml-2 text-brand-600">• View detailed report</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <RatingBadge rating={suggestion.dftOverallRating} size="sm" />
                              </div>
                            </button>
                          );
                        })
                      ) : searchQuery.length >= 3 ? (
                        <div className="p-6 text-center">
                          <div className="text-surface-400 mb-4">
                            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm font-medium text-surface-600">No councils found for "{searchQuery}"</p>
                            <p className="text-xs text-surface-500 mt-1">Try checking your spelling or searching for a different name</p>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link
                              to="/road-maintenance-report"
                              onClick={() => setShowAutocomplete(false)}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              View League Table
                            </Link>
                            <button
                              onClick={handleClearSearch}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-surface-700 bg-surface-100 rounded-lg hover:bg-surface-200 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Clear &amp; Try Again
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="w-full sm:w-auto px-4 py-3 min-h-[48px] rounded-xl border border-surface-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                  >
                    <option value="all">All Regions</option>
                    {[...data.regions].sort().map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>

                  <select
                    aria-label="Filter by overall rating"
                    value={selectedRating}
                    onChange={(e) => setSelectedRating(e.target.value as typeof selectedRating)}
                    className="w-full sm:w-auto px-4 py-3 min-h-[48px] rounded-xl border border-surface-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                  >
                    <option value="all">All Ratings</option>
                    <option value="green">Green</option>
                    <option value="amber">Amber</option>
                    <option value="red">Red</option>
                  </select>
                  
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [newSortBy, newSortOrder] = e.target.value.split('-') as ['name' | 'overall', 'asc' | 'desc'];
                      setSortBy(newSortBy);
                      setSortOrder(newSortOrder);
                    }}
                    className="w-full sm:w-auto px-4 py-3 min-h-[48px] rounded-xl border border-surface-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                  >
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="overall-asc">Best rating first</option>
                    <option value="overall-desc">RED band first</option>
                  </select>
                </div>
              </div>

              {/* Filtered stats */}
              {(selectedRegion !== 'all' || selectedRating !== 'all' || searchQuery) && (
                <div className="flex items-center gap-4 mb-6 text-sm text-surface-600">
                  <span className="sr-only">
                    Filtered by {selectedRegion === 'all' ? 'all regions' : selectedRegion} and{' '}
                    {selectedRating === 'all' ? 'all ratings' : selectedRating}
                  </span>
                  <span>
                    Showing <strong className="text-brand-900">{filteredStats.total}</strong> authorities
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-emerald-500" /> {filteredStats.green}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-amber-500" /> {filteredStats.amber}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-rose-500" /> {filteredStats.red}
                  </span>
                </div>
              )}

              {/* Table */}
              <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <caption className="sr-only">English Local Highway Authority DfT Road Maintenance Ratings 2025-2026 - searchable table showing overall, condition, spend, and best practice rating bands</caption>
                    <thead>
                      <tr className="bg-surface-50 border-b border-surface-200">
                        <th className="text-left px-6 py-4 text-sm font-semibold text-surface-900">
                          Local Highway Authority
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-surface-900 hidden md:table-cell">
                          Region
                        </th>
                        <th className="text-center px-4 py-4 text-sm font-semibold text-surface-900">
                          Overall
                        </th>
                        <th className="text-center px-4 py-4 text-sm font-semibold text-surface-900 hidden lg:table-cell">
                          Condition
                        </th>
                        <th className="text-center px-4 py-4 text-sm font-semibold text-surface-900 hidden lg:table-cell">
                          Spend
                        </th>
                        <th className="text-center px-4 py-4 text-sm font-semibold text-surface-900 hidden lg:table-cell">
                          Best Practice
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-100">
                      {filteredAuthorities.map((auth) => {
                        const hasPage = councilPages[auth.name];
                        const displayName = getCouncilDisplayName(auth.name);
                        return (
                          <tr key={auth.id} className="hover:bg-surface-50 transition-colors">
                            <td className="px-6 py-4">
                              {hasPage ? (
                                <Link to={hasPage} className="font-medium text-brand-600 hover:text-brand-800 hover:underline flex items-center gap-1">
                                  {displayName}
                                  <svg className="w-4 h-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </Link>
                              ) : (
                                <div className="font-medium text-brand-950">{displayName}</div>
                              )}
                              <div className="text-xs text-surface-500 md:hidden">{auth.region}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-surface-600 hidden md:table-cell">
                              {auth.region}
                            </td>
                            <td className="px-4 py-4 text-center">
                              <RatingBadge rating={auth.dftOverallRating} size="md" />
                            </td>
                            <td className="px-4 py-4 text-center hidden lg:table-cell">
                              <RatingBadge rating={auth.dftConditionRating} />
                            </td>
                            <td className="px-4 py-4 text-center hidden lg:table-cell">
                              <RatingBadge rating={auth.dftSpendRating} />
                            </td>
                            <td className="px-4 py-4 text-center hidden lg:table-cell">
                              <RatingBadge rating={auth.dftBestPracticeRating} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {filteredAuthorities.length === 0 && (
                  <div className="text-center py-16 px-4">
                    <div className="max-w-md mx-auto">
                      <svg className="w-16 h-16 mx-auto text-surface-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-surface-900 mb-2">
                        No councils found
                      </h3>
                      <p className="text-surface-500 mb-6">
                        {searchQuery 
                          ? `We couldn't find any councils matching "${searchQuery}". Try checking the spelling or browse all councils below.`
                          : selectedRegion !== 'all'
                          ? `No councils found in ${selectedRegion}. Try selecting a different region.`
                          : 'No results found with the current filters.'}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                          to="/road-maintenance-report"
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[48px] text-sm font-semibold text-white bg-brand-600 rounded-xl hover:bg-brand-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          View National League Table
                        </Link>
                        {(searchQuery || selectedRegion !== 'all' || selectedRating !== 'all') && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setSelectedRegion('all');
                              setSelectedRating('all');
                            }}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[48px] text-sm font-semibold text-brand-700 bg-brand-50 rounded-xl hover:bg-brand-100 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer note */}
              <p className="mt-6 text-sm text-surface-500 text-center">
                Data source: Department for Transport Local Highway Authority Road Maintenance Ratings {data?.ratingYear}.{' '}
                <a 
                  href="https://www.gov.uk/government/publications/local-highway-authority-road-maintenance-ratings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  View official publication →
                </a>
              </p>
          </>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-brand-50 to-accent-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Damaged by a pothole?
          </h2>
          <p className="text-lg text-surface-700 mb-8">
            Mac can include your council's source rating as background context in the documents you review.
          </p>
          <Link
            to="/check"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
          >
            Start Your Claim
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
